import { type CONVEYANCE_TYPES, CONVEYANCE_TYPES as CONVEYANCE_TYPES_ENUM } from '@koala/sdk/v4';
import { useState } from 'react';
import { Modal } from '@/components/uielements/modal';
import { StyledPrimaryButton } from '@/components/uielements/primaryButton/styles';
import { HandoffTimePicker } from '@/features/handoff/time-picker';
import { useLocation } from '@/features/locations/service';
import { useDispatch, useSelector } from '@/redux';
import { useBasketMutating } from '@/hooks/useBasketMutating';
import basketActions from '@/redux/basket/actions';
import conveyanceModeActions from '@/redux/conveyanceMode/actions';
import { selectConveyance } from '@/redux/conveyanceMode/reducer';
import { fetchMenu } from '@/services/locations.service';
import { buildCommitConfirmation } from '@/features/handoff/time-picker/commit-confirmation';
import { basketCheckInvalidItems } from '@/utils/basket';
import { getStoreTimeInfo } from '@/utils/locations';
import { sessionStorageBasket } from '@/utils/global';
import orderStatusActions from '@/redux/orderStatus/actions';
import { isAsapValue } from '@/utils/conveyance';

export function TimePicker({
  activeConveyance,
  onClose,
}: {
  activeConveyance: CONVEYANCE_TYPES;
  onClose: () => void;
}) {
  const dispatch = useDispatch();
  const [resetOrderTime, setResetOrderTime] = useState<string>();
  const { checkoutBasket } = useSelector((state) => state.app.basket);
  const { type, time_wanted, location: locationId } = useSelector(selectConveyance);
  const basketMutating = useBasketMutating();
  const { data: location, isSuccess } = useLocation(locationId);
  const { storeTimezone } = getStoreTimeInfo(location?.data, activeConveyance);
  const requiresDeliveryAddress = Boolean(
    type === CONVEYANCE_TYPES_ENUM.DELIVERY &&
      location?.data?.supports_delivery &&
      !location?.data?.supports_pick_up,
  );

  /**
   * Applies the new time, unless items in the basket are unavailable at it — in
   * that case the Restart order confirmation below takes over and this returns
   * true, meaning "not applied yet, the diner still has to answer".
   *
   * No `if (basketMutating) return;` here on purpose. `isSubmitting` below already
   * disables the picker's submit button, and dropping the change outright would
   * lose the diner's chosen time while the UI kept showing it — the divergence
   * TEAM-14696 is about. `basketMutationGuard` defers the dispatch instead and
   * replays it once the in-flight write settles.
   */
  const handleTimeChange = async (selectedDateTime: string): Promise<boolean> => {
    const localBasket = sessionStorageBasket.get();
    if (!location) return false;

    if (localBasket?.content) {
      const [menuResponse] = await fetchMenu({
        id: location.data.id,
        wantedAt: selectedDateTime,
      });

      // Ensure all items in cart exists on a newly fetched menu or else show restart order confirmation
      const invalidItems = basketCheckInvalidItems(localBasket.content.basket_items, menuResponse);
      if (invalidItems.length > 0) {
        setResetOrderTime(selectedDateTime);
        return true;
      }
    }
    // Confirmation copy travels with the commit action so the toast is dispatched from
    // the saga's success branch, never ahead of a failure (TEAM-14387 AC6). Nothing is
    // attached on the ASAP branch, which has no clock time to confirm (AC5), and the
    // invalid-items branch above returns before any dispatch — so deferring to the
    // Restart order confirmation cannot produce a premature toast either.
    // activeConveyance, not `type`: `type` is basket.fulfillment?.type ?? conveyanceMode.type,
    // which lags the toggled conveyance whenever the committed fulfillment has not caught
    // up — selectConveyance models that divergence explicitly as order.matchesCurrent. The
    // picker on screen renders with activeConveyance, so the toast has to agree with it or
    // it can say "Pickup" over a Delivery picker (AC3).
    const confirmation = buildCommitConfirmation(selectedDateTime, activeConveyance, storeTimezone);

    if (checkoutBasket?.id) {
      isAsapValue(selectedDateTime)
        ? dispatch(orderStatusActions.wantedAtBasketRemove())
        : dispatch(
            orderStatusActions.wantedAtBasketSet(
              selectedDateTime,
              undefined,
              undefined,
              confirmation ?? undefined,
            ),
          );
    } else {
      dispatch(
        conveyanceModeActions.setWantedTime(
          selectedDateTime,
          location.data.id,
          type,
          confirmation ?? undefined,
        ),
      );
    }
    return false;
  };

  if (!isSuccess) {
    return null;
  }

  const submitRestartTimeChange = (toggle: () => void) => {
    toggle();
    dispatch(basketActions.destroyBasket());
    dispatch(orderStatusActions.destroyOrder());

    // AC7: the confirmation fires after the diner confirms the restart, not before —
    // it rides this dispatch and is toasted by the saga once the time actually applies.
    dispatch(
      conveyanceModeActions.setWantedTime(
        resetOrderTime,
        location.data.id,
        type,
        buildCommitConfirmation(resetOrderTime, activeConveyance, storeTimezone) ?? undefined,
      ),
    );
    // The confirmation is resolved and the time is applied, so close the picker the
    // same way a plain commit does. Declining ("Keep existing") deliberately leaves
    // it open instead, so the diner can choose another time.
    onClose?.();
  };

  const submitTimeChange = async (selectedWantedAt: string) => {
    const awaitingRestartConfirmation = await handleTimeChange(selectedWantedAt);
    // The Restart order confirmation renders inside this component, so closing the
    // popover here would unmount it the instant it was requested — the diner would
    // see the picker shut with the time silently unchanged (TEAM-14386 AC8).
    if (!awaitingRestartConfirmation) {
      onClose?.();
    }
    // Reported back to the picker so it keeps the per-field touched flags instead of
    // clearing them — that is what makes the CHANGED marker and the warning reappear
    // if the diner declines with "Keep existing" (TEAM-14386 AC3). The treatment is
    // not visible while the confirmation is open, because `value` below is
    // `resetOrderTime ?? time_wanted` and the deferral has just set `resetOrderTime`
    // to the candidate time; the confirmation overlays the picker anyway.
    return awaitingRestartConfirmation;
  };

  return (
    <>
      <HandoffTimePicker
        key={`handoff-time-picker-${activeConveyance}`}
        stack={false}
        location={location.data}
        handoffType={activeConveyance}
        value={resetOrderTime ?? time_wanted}
        onCancel={requiresDeliveryAddress ? undefined : onClose}
        onSubmit={submitTimeChange}
        isSubmitting={basketMutating}
        showPendingChange
      />

      <Modal.Container
        requestVisibility={Boolean(resetOrderTime)}
        onCancel={() => {
          setResetOrderTime(undefined);
        }}
      >
        {(toggle) => (
          <>
            <Modal.Title>Restart order</Modal.Title>
            <Modal.Content>
              <p>
                Some items in your basket are unavailable for your new order time. Would you like to
                proceed with changing your order time?
              </p>
            </Modal.Content>

            <Modal.Footer>
              <Modal.Cancel>Keep existing</Modal.Cancel>

              <StyledPrimaryButton
                data-css-override="StyledPrimaryButton"
                type="submit"
                onClick={() => submitRestartTimeChange(toggle)}
              >
                Change Time
              </StyledPrimaryButton>
            </Modal.Footer>
          </>
        )}
      </Modal.Container>
    </>
  );
}
